/* eslint-disable react/forbid-foreign-prop-types */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, IconButton, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { abundanceComponents } from '../../../../../abundanceEngine/abundanceComponentsRegister'
import { useComponentDoc } from '../../../../../api/abundanceEngine'
import { extractTypes } from './helpers'
import PropsTable from './PropsTable'

const useStyles = makeStyles((theme) => ({
  markdown: {
    border: '1px solid var(--color-neutral-400)',
    borderRadius: 'var(--dim-3)',
    padding: 'var(--dim-3)',
    '& h1': {
      fontSize: '1.15rem',
      margin: 'var(--dim-2) 0 var(--dim-1)'
    },
    '& h2': {
      fontSize: '1rem',
      margin: 'var(--dim-2) 0 var(--dim-1)'
    },
    '& h3': {
      fontSize: '0.85rem',
      margin: 'var(--dim-2) 0 var(--dim-1)'
    },
    '& pre': {
      overflowX: 'auto',
      background: theme.palette.grey[100],
      padding: theme.spacing(1.5),
      borderRadius: 4
    },
    '& code': {
      background: theme.palette.grey[100],
      padding: '1px 4px',
      borderRadius: 3
    }
  }
}))

function ComponentDocsPanel ({ componentName, onClose }) {
  const classes = useStyles()
  const { doc: publishedDoc, isLoading, isError } = useComponentDoc(componentName)

  const componentMetadata = useMemo(() => {
    if (!componentName) return null
    const component = abundanceComponents[componentName]
    if (!component) return null
    return {
      name: componentName,
      propTypes: extractTypes(component),
      defaultProps: component?.defaultProps || null
    }
  }, [componentName])

  if (!componentMetadata) {
    return (
      <div className='pt-3 h-full flex flex-col'>
        <div className='flex-1 flex flex-col radius-lg border border-neutral-300 p-4 gap-lg'>
          <div className='flex items-start justify-between gap-sm'>
            <div className='font-size-md'>{componentName}</div>
            {onClose && (
              <IconButton size='small' onClick={onClose} aria-label='close'>
                <CloseIcon fontSize='small' />
              </IconButton>
            )}
          </div>
          <div className='opacity-lowlighted font-size-xs'>
            Component metadata is unavailable.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='pt-3 h-full flex flex-col'>
      <div
        className='flex-1 flex flex-col radius-lg border border-neutral-300 p-4 gap-lg'
        style={{
          overflow: 'auto'
        }}
      >
        <div className='flex items-start justify-between gap-sm'>
          <div className='flex-1'>
            <div className='font-size-md'>{componentMetadata.name}</div>
            <div className='opacity-lowlighted font-size-xs'>
              {publishedDoc?.sourcePath
                ? `Source: ${publishedDoc.sourcePath}`
                : 'Auto-detected props (no published docs)'}
            </div>
          </div>

          {onClose && (
            <IconButton size='small' onClick={onClose} aria-label='close'>
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
        </div>

        <div className='flex flex-col gap-sm'>
          <div className='font-size-sm mb-0'>Properties</div>
          <PropsTable
            value={componentMetadata.propTypes}
            renderLabel={(key, val) => (
              <>{key}{val.required ? <span>*</span> : null}</>
            )}
            renderValue={(key, val) => val.type}
          />
        </div>

        <div className='flex flex-col gap-sm'>
          <div className='font-size-sm mb-0'>Default Values</div>
          <PropsTable
            value={componentMetadata.defaultProps}
            renderValue={(key, val) => JSON.stringify(val, null, 2)}
          />
        </div>

        <div className='flex flex-col gap-sm'>
          <div className='font-size-sm mb-0'>Documentation</div>
          {isLoading
            ? (
              <div className='flex flex-1 gap-sm items-center'>
                <CircularProgress size={16} />
                <span className='font-size-xs opacity-lowlighted'>Loading docs...</span>
              </div>
            )
            : isError
              ? (
                <div className='opacity-lowlighted font-size-xs'>
                  Failed to load documentation.
                </div>
              )
              : publishedDoc?.markdown
                ? (
                  <div className={classes.markdown}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {publishedDoc.markdown}
                    </ReactMarkdown>
                  </div>
                )
                : (
                  <div className='opacity-lowlighted font-size-xs'>
                    No published documentation available for this component.
                  </div>
                )}
        </div>
      </div>
    </div>
  )
}

ComponentDocsPanel.propTypes = {
  componentName: PropTypes.string,
  onClose: PropTypes.func
}

ComponentDocsPanel.defaultProps = {
  componentName: undefined,
  onClose: undefined
}

export default ComponentDocsPanel
